/**
 * Marketing footer: 4 columns on desktop, 2×2 on mobile. Legal links live
 * only in the bottom bar.
 */

'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Linkedin, Twitter, Instagram } from 'lucide-react';

type FooterLink = {
  label: string;
  href: string;
  hideOnMobile?: boolean;
  // Renders in yellow with a stronger hover — used for "Browse all" CTAs
  isCta?: boolean;
};

type FooterColumn = {
  title: string;
  titleHref?: string;
  links: FooterLink[];
};

const footerColumns: FooterColumn[] = [
  {
    title: 'Product',
    links: [
      { label: 'For Counselors', href: '/for-counselors' },
      { label: 'For Schools', href: '/for-schools' },
      { label: 'For Students', href: '/for-students' },
      { label: 'For Industry', href: '/for-industry' },
      { label: 'Features', href: '/features' },
      { label: 'Pricing', href: '/pricing' }
    ]
  },
  {
    title: 'Company',
    links: [
      { label: 'About Us', href: '/about' },
      { label: 'Case Studies', href: '/case-studies' },
      { label: 'Blog', href: '/blog' },
      { label: 'Upcoming Events', href: '/upcoming-events' },
      { label: 'Contact', href: '/request-demo' }
    ]
  },
  {
    title: 'Toolkit',
    titleHref: '/toolkit',
    links: [
      {
        label: 'For Staff — Staffing Calculator',
        href: '/toolkit/staffing-calculator'
      },
      {
        label: 'For Parents — Career Conversation Kit',
        href: '/toolkit/parent-conversation-kit'
      },
      {
        label: 'For Students — Career Quiz',
        href: '/toolkit/career-quiz'
      },
      {
        label: 'QLD Skills & Careers Outlook',
        href: '/toolkit/queensland-skills-careers'
      },
      {
        label: 'QLD Career Readiness Assessment',
        href: '/toolkit/queensland-career-readiness-assessment'
      },
      {
        label: 'Browse all free tools →',
        href: '/toolkit',
        isCta: true
      }
    ]
  },
  {
    title: 'Support',
    links: [
      { label: 'Product Tour', href: '/product-tour' },
      { label: 'Demo', href: '/demo' },
      { label: 'Deck', href: '/deck' },
      { label: 'Security', href: '/security' },
      { label: 'Data Processing Agreement', href: '/dpa' }
    ]
  }
];

const socialLinks = [
  {
    href: 'https://www.linkedin.com/company/texinc/',
    label: 'LinkedIn',
    Icon: Linkedin
  },
  {
    href: 'https://x.com/texlearn',
    label: 'Twitter / X',
    Icon: Twitter
  },
  {
    href: 'https://www.instagram.com/texlearn/',
    label: 'Instagram',
    Icon: Instagram
  }
];

export function MarketingFooter() {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      style={{
        backgroundColor: 'var(--mktg-dark)',
        color: 'var(--mktg-white)'
      }}
      aria-label='Site footer'
    >
      <div
        className='mx-auto px-6 pt-12 pb-10 md:px-[72px]'
        style={{ maxWidth: 'var(--mktg-container-max, 1440px)' }}
      >
        <div className='mb-14 flex flex-col gap-10 md:flex-row md:items-start md:gap-16'>
          <div className='shrink-0 md:w-[200px]'>
            <Link href='/' aria-label='TEX for Schools home'>
              <Image
                src='/logos/tex/tex-rectangle-dark-left-aligned.png'
                alt='TEX for Schools'
                width={80}
                height={68}
                className='object-contain'
              />
            </Link>
            <p
              className='mt-4 text-[13px] leading-relaxed'
              style={{ color: 'var(--mktg-white-50)' }}
            >
              Every Student Supported. Every Outcome Tracked.
            </p>
            <div className='mt-6 flex items-center gap-4'>
              {socialLinks.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label={label}
                  className='flex items-center transition-colors duration-150'
                  style={{ color: 'var(--mktg-white-50)' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color =
                      'var(--mktg-yellow)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color =
                      'var(--mktg-white-50)';
                  }}
                >
                  <Icon className='h-5 w-5' />
                </a>
              ))}
            </div>
          </div>

          <div className='grid flex-1 grid-cols-2 gap-x-8 gap-y-10 sm:gap-x-12 md:flex md:justify-end md:gap-12'>
            {footerColumns.map((col) => (
              <div key={col.title} className='min-w-[110px]'>
                {col.titleHref ? (
                  <Link
                    href={col.titleHref}
                    className='mb-5 block text-[13px] font-bold tracking-[0.06em] uppercase no-underline transition-colors duration-150'
                    style={{ color: 'var(--mktg-white-40)' }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.color =
                        'var(--mktg-white-65)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.color =
                        'var(--mktg-white-40)';
                    }}
                  >
                    {col.title}
                  </Link>
                ) : (
                  <p
                    className='mb-5 text-[13px] font-bold tracking-[0.06em] uppercase'
                    style={{ color: 'var(--mktg-white-40)' }}
                  >
                    {col.title}
                  </p>
                )}
                <ul className='m-0 list-none space-y-3 p-0'>
                  {col.links.map((link) => (
                    <li
                      key={link.href}
                      className={link.hideOnMobile ? 'hidden sm:block' : ''}
                    >
                      {link.isCta ? (
                        <Link
                          href={link.href}
                          className='text-[14px] font-semibold no-underline transition-colors duration-150'
                          style={{ color: 'var(--mktg-yellow)' }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.color =
                              'var(--mktg-yellow-dk)';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.color =
                              'var(--mktg-yellow)';
                          }}
                        >
                          {link.label}
                        </Link>
                      ) : (
                        <Link
                          href={link.href}
                          className='text-[14px] no-underline transition-colors duration-150'
                          style={{ color: 'var(--mktg-white-65)' }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.color =
                              'var(--mktg-white)';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.color =
                              'var(--mktg-white-65)';
                          }}
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div
          className='flex flex-col gap-4 border-t pt-6 sm:flex-row sm:items-center sm:justify-between'
          style={{ borderColor: 'rgba(255,255,255,0.1)' }}
        >
          <p
            className='m-0 text-[13px]'
            style={{ color: 'var(--mktg-white-40)' }}
          >
            &copy; {currentYear} TEX. All rights reserved.
          </p>
          <div className='flex gap-6'>
            {[
              { label: 'Privacy Policy', href: '/privacy' },
              { label: 'Terms of Service', href: '/terms' },
              { label: 'DPA', href: '/dpa' }
            ].map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className='text-[13px] no-underline transition-colors duration-150'
                style={{ color: 'var(--mktg-white-40)' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.color =
                    'var(--mktg-white-75)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.color =
                    'var(--mktg-white-40)';
                }}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
